import ProductsService from "@/api/services/products";
import { toCamelCase } from "@/utils/utils";

const initialState = {
  selectedFilters: {
    approvalStatus: [],
    contractStatus: [],
    enabled: [],
    productType: [],
    category: [],
  },
  keywords: "",
  minPrice: null,
  maxPrice: null,
  products: [],
  productTypes: [],
  currentPage: 1,
  totalPages: 0,
  totalCount: 0,
  selectedStore: null,
};

export default {
  namespaced: true,

  state: initialState,

  getters: {
    isOnlineProduct: () => (product) => {
      return product != null && product.source !== "offline";
    },
    isProductApprovable: (_, getters) => (product) => {
      return getters.isOnlineProduct(product) && product.approvalStatus !== "approved";
    },
    isProductUnapprovable: (_, getters) => (product) => {
      return getters.isOnlineProduct(product) && product.approvalStatus !== "refused" && product.enabled === false;
    },
    isProductMatchable: (_, getters) => (product) => {
      return (
        getters.isOnlineProduct(product) &&
        product.contractStatus === "pending" &&
        product.approvalStatus === "approved"
      );
    },
    isProductEnablable: (_, getters) => (product) => {
      return (
        getters.isOnlineProduct(product) &&
        product.contractStatus === "matched" &&
        product.approvalStatus === "approved" &&
        product.enabled === false
      );
    },
    isProductDisablable: (_, getters) => (product) => {
      return (
        getters.isOnlineProduct(product) &&
        product.contractStatus === "matched" &&
        product.approvalStatus === "approved" &&
        product.enabled === true
      );
    },
    approvalStatusStyle: () => (status) => {
      switch (status) {
        case "approved":
          return "bg-green-200 text-green-800";
        case "to_be_approved":
          return "bg-yellow-200 text-yellow-800";
        case "refused":
          return "bg-red-200 text-red-800";
        default:
          return "bg-gray-200 text-gray-600";
      }
    },
    contractStatusStyle: () => (status) => {
      switch (status) {
        case "matched":
          return "bg-green-200 text-green-800";
        case "pending":
          return "bg-yellow-200 text-yellow-800";
        case "non_warrantable":
          return "bg-red-200 text-red-800";
        default:
          return "bg-gray-200 text-gray-600";
      }
    },
    approvalStatusListStyle: () => (status) => {
      switch (status) {
        case "to_be_approved":
          return "bg-yellow-100 text-yellow-800 border border-yellow-300";
        case "approved":
          return "bg-green-100 text-green-800 border border-green-300";
        case "refused":
          return "bg-red-100 text-red-800 border border-red-300";
        default:
          return "bg-blue-100 text-blue-800 border border-blue-300";
      }
    },
    contractStatusListStyle: () => (status) => {
      switch (status) {
        case "pending":
          return "bg-yellow-100 text-yellow-800 border border-yellow-300";
        case "matched":
          return "bg-green-100 text-green-800 border border-green-300";
        case "non_warrantable":
          return "bg-red-100 text-red-800 border border-red-300";
        default:
          return "bg-blue-100 text-blue-800 border border-blue-300";
      }
    },
    enabledStyle: () => (isEnabled) => {
      return isEnabled
        ? "bg-green-100 text-green-800 border border-green-300"
        : "bg-red-100 text-red-800 border border-red-300";
    },
    sourceStyle: () => (source) => {
      switch (source) {
        case "offline":
          return "bg-blue-100 text-blue-800 border border-blue-300";
        default:
          return "bg-pink-100 text-pink-800 border border-pink-300";
      }
    },
    variantStatusNotValid: () => (product) => {
      return Boolean(
        product?.variants?.some(
          (variant) =>
            variant &&
            (variant.approvalStatus !== product.approvalStatus ||
              variant.contractStatus !== product.contractStatus ||
              variant.enabled !== product.enabled),
        ),
      );
    },
  },

  mutations: {
    setSelectedStore(state, store) {
      state.selectedStore = store;
    },

    setMinPrice(state, minPrice) {
      state.minPrice = minPrice;
    },

    setMaxPrice(state, maxPrice) {
      state.maxPrice = maxPrice;
    },

    setProducts(state, { products, totalCount, totalPages, currentPage, productTypes }) {
      state.products = products;
      state.totalCount = totalCount || 0;
      state.totalPages = totalPages || 0;
      state.currentPage = currentPage || 1;
      state.productTypes = productTypes || [];
    },

    setFilter(state, { filterName, filterOptions }) {
      state.selectedFilters[filterName] = filterOptions;
    },

    setKeywords(state, keywords) {
      state.keywords = keywords;
    },
  },

  actions: {
    async fetchIndex(
      { state, commit },
      { page, includes = ["business_vertical", "variants.business_vertical", "variants"] },
    ) {
      if (!state.selectedStore) return;

      const response = await ProductsService.fetchIndex({
        includes,
        storeId: [state.selectedStore.id],
        page: page,
        keywords: state.keywords,
        minPrice: state.minPrice,
        maxPrice: state.maxPrice,
        online: true,
        ...state.selectedFilters,
      });

      if (!response?.success) return response;

      const responseData = toCamelCase(response.data);

      commit("setProducts", {
        products: responseData.records,
        totalCount: responseData.page.totalCount,
        totalPages: responseData.page.totalPages,
        currentPage: responseData.page.currentPage,
        productTypes: responseData.filters.productTypes,
      });

      return response;
    },

    async fetch(_, { id, includes = [] }) {
      const response = await ProductsService.fetch(id, { includes });
      if (!response?.success) return;

      return toCamelCase(response.data);
    },

    updateFilter({ commit, dispatch }, { filterName, filterOptions }) {
      commit("setFilter", { filterName, filterOptions });
      return dispatch("fetchIndex", {
        page: 1,
      });
    },

    updatePriceFilter({ commit, dispatch }, { minPrice, maxPrice }) {
      commit("setMaxPrice", maxPrice);
      commit("setMinPrice", minPrice);
      return dispatch("fetchIndex", {
        page: 1,
      });
    },

    updateSearch({ commit, dispatch }, { keywords }) {
      commit("setKeywords", keywords);
      return dispatch("fetchIndex", {
        page: 1,
      });
    },

    async approveProducts({ state, dispatch }, productIds) {
      const response = await ProductsService.approveProducts(productIds);
      if (!response?.success) return response;

      dispatch("fetchIndex", { page: state.currentPage });
    },

    async unApproveProducts({ state, dispatch }, productIds) {
      const response = await ProductsService.unApproveProducts(productIds);
      if (!response?.success) return response;

      dispatch("fetchIndex", { page: state.currentPage });
    },

    async matchProducts({ state, dispatch }, productIds) {
      const response = await ProductsService.matchProducts(productIds);
      if (!response?.success) return response;

      dispatch("fetchIndex", { page: state.currentPage });
    },

    async unmatchProducts({ state, dispatch }, productIds) {
      const response = await ProductsService.unmatchProducts(productIds);
      if (!response?.success) return response;

      dispatch("fetchIndex", { page: state.currentPage });
    },

    async approveMatchProducts({ state, dispatch }, productIds) {
      const response = await ProductsService.approveMatchProducts(productIds);
      if (!response?.success) return response;

      dispatch("fetchIndex", { page: state.currentPage });
    },

    async approveMatchPublishProducts({ state, dispatch }, productIds) {
      const response = await ProductsService.approveMatchPublishProducts(productIds);
      if (!response?.success) return response;

      dispatch("fetchIndex", { page: state.currentPage });
    },

    async enableProducts({ state, dispatch }, productIds) {
      const response = await ProductsService.enableProducts(productIds);
      if (!response?.success) return response;

      dispatch("fetchIndex", { page: state.currentPage });
    },

    async disableProducts({ state, dispatch }, productIds) {
      const response = await ProductsService.disableProducts(productIds);
      if (!response?.success) return response;

      dispatch("fetchIndex", { page: state.currentPage });
    },

    async updateProductCategory({ state, dispatch }, { productIds, category }) {
      const response = await ProductsService.updateProductCategory({ productIds, category });
      if (!response?.success) return response;

      dispatch("fetchIndex", { page: state.currentPage });
    },
  },
};
